import type { OrderStrategiesEnum, ParameterKeysEnum } from '@src/tokens/orderStrategy';
import type {
  Allocation,
  AllocationValueTypeEnum,
  CamelCased,
  DecisionStatusEnum,
  Duration,
  MarketOrder,
  OrdTypeEnum,
  PriceProtectionEnum,
  PricingMode,
  ReduceFirstEnum,
  ReduceOnlyEnum,
  SideEnum,
  UnifiedLiquidityEnum,
} from '@talos/kyoko';
import type { MarketSelectorItem } from '../components/MarketSelector/types';

// Hardcoded constant used by the backend as the semantic Market key for mutlileg orders on our "internal market"
export const MULTILEG_MARKET = 'multileg';
export const MULTILEG_MARKET_ACCOUNT = 'multileg/multileg';

/** The backend definition of multileg LegParams */
export interface LegParams {
  Symbol?: string;
  Markets: string[];
  Initiating: boolean;
  LegIndex: number | undefined;
  HaircutRatio?: string;
}

/** A frontend extension of LegParams. Used to store some state to help us as we build the LegParams in forms. */
export type LegFormParams = LegParams & {
  initialized: boolean;
  items?: MarketSelectorItem[];
};

export type OMSFormOrderParameters = CamelCased<
  {
    [ParameterKeysEnum.StartTime]?: string | Date | Duration | null;
    [ParameterKeysEnum.EndTime]?: string | Date | Duration | null;
    [ParameterKeysEnum.Duration]?: string | null;
    [ParameterKeysEnum.PriceOffset]?: string | null;
    [ParameterKeysEnum.ShowQty]?: string | null;
    [ParameterKeysEnum.PriceProtection]?: PriceProtectionEnum | null;
    [ParameterKeysEnum.UnifiedLiquidity]?: UnifiedLiquidityEnum | null;
    [ParameterKeysEnum.ReduceOnly]?: ReduceOnlyEnum | null;
    [ParameterKeysEnum.ReduceFirst]?: ReduceFirstEnum | null;
    [key: string]: unknown | string | undefined;
  },
  false
>;

export interface OMSForm {
  // Order and RFQ Form params
  symbol: string;
  quantity: string;
  price: string;
  group?: string;
  ordType?: OrdTypeEnum;
  orderCurrency: string;
  orderSide?: SideEnum;
  subAccountAllocations?: Allocation[];
  orderMarketAccounts: string[];
  strategy?: OrderStrategiesEnum;
  parameters: OMSFormOrderParameters;
  duration?: string;
  customDuration?: boolean;
  startTime?: Date;
  submitTime?: Date;
  endTime?: Date;
  initialDecisionStatus?: DecisionStatusEnum;
  allocationValueType?: AllocationValueTypeEnum;
  // Sales RFQ Params
  customer?: string;
  customerQuantity?: string;
  customerMarketAccount?: string;
  // Idk why this is also tacked on here....
  trade?: Partial<ManualTradeForm>;
  marketOrder?: MarketOrder;
  comment?: string;
  pricingMode?: PricingMode;
  //Multileg specific
  legParams?: LegParams[];
}

// This might be able to get rid of
export interface ManualTradeForm {
  side: SideEnum;
  transactTime: Date;
  symbol?: string;
  subAccount?: string;
  quantity?: string;
  amount?: string;
  price?: string;
  subAccountId?: number;
  fee?: string;
  feeCurrency?: string;
  market?: string;
  marketAccount?: string;
  comments?: string;
  group?: string;
  ordType?: OrdTypeEnum;
  subAccountAllocations?: Allocation[];
  allocationValueType?: AllocationValueTypeEnum;
  // We allow talos admins + support to modify regular (non-manual, non-orphan) trades. In which case, this property would be defined.
  orderId?: string;
}
